import { complement, mapObjIndexed, reject } from 'ramda';
import { noop } from 'js/utils/methods.util';
import { getStartOfTheDay, getEndOfTheDay } from 'js/utils/date.util';

export const isEmpty = value => value == null || value === '' || value === false;
export const isNotEmpty = complement(isEmpty);
export const isNonZero = value => Number(value) !== 0;

export const defaults = {
    required: {
        predicate: isNotEmpty,
        error: 'playtech.mobile.system.tooltip.required'
    },
    number: {
        re: /^-?((\d+\.?\d*$)|(\.\d+$))/,
        error: 'playtech.mobile.system.tooltip.number'
    },
    int: {
        re: /^-?\d+$/,
        error: 'playtech.mobile.system.tooltip.int'
    },
    positive: {
        error: 'playtech.mobile.system.tooltip.positive'
    },
    negative: {
        error: 'playtech.mobile.system.tooltip.negative'
    },
    money: {
        re: /^((\d+[.,]?\d{0,2}$)|([.,]\d{1,2}$))/,
        error: 'playtech.mobile.system.tooltip.amount'
    },
    length: {
        min: 0,
        max: Infinity,
        error: 'playtech.mobile.system.tooltip.length'
    },
    range: {
        min: -Infinity,
        max: Infinity,
        includeMin: true,
        includeMax: true,
        exceptions: [],
        error: 'playtech.mobile.system.tooltip.range'
    },
    regexp: {
        re: '',
        error: 'playtech.mobile.system.tooltip.regexp'
    },
    email: {
        // src: https://www.regular-expressions.info/email.html
        re: /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i,
        error: 'playtech.mobile.system.tooltip.email'
    },
    oneOf: {
        fields: [],
        error: 'playtech.mobile.system.tooltip.oneOf'
    },
    oneOfBy: {
        fields: [],
        predicate: isNotEmpty,
        error: 'playtech.mobile.system.tooltip.oneOf'
    },
    ascending: {
        fields: [],
        eq: true,
        error: 'playtech.mobile.system.tooltip.ascending',
        exceptions: []
    },
    equal: {
        field: null,
        error: 'playtech.mobile.system.tooltip.equal'
    },
    confirm: {
        field: null,
        error: 'playtech.mobile.system.tooltip.confirm'
    },
    fn: {
        fn: noop,
        error: 'playtech.mobile.system.error.default'
    }
};

/**
 * @param {RegExp|String|Array} re
 * @returns {RegExp}
 */
const getRegexp = re => re instanceof RegExp
    ? re
    : typeof re === 'string'
        ? new RegExp(re)
        : new RegExp(...re);

export const withDefaultOptions = (methods, defaultOptions = defaults) =>
    mapObjIndexed(
        (methodCreator, name) => (options) => {
            const mergedOptions = {
                ...defaultOptions[name],
                ...options
            };
            return methodCreator(mergedOptions);
        },
        methods
    );

// region Validation methods

export const required = ({ predicate, error }) => value =>
    predicate(value)
        ? null
        : [error];

export const regexp = ({ re, error } = defaults.regexp) => {
    const regExp = getRegexp(re);

    return (value) => {
        if (isEmpty(value)) {
            return null;
        }

        return regExp.test(value) ? null : [error];
    };
};

export const number = ({ re, error }) => regexp({ re, error });

export const int = ({ re, error }) => regexp({ re, error });

export const positive = ({ error }) => value => Number(value) >= 0 ? null : [error];

export const negative = ({ error }) => value => Number(value) <= 0 ? null : [error];

export const range = ({ min: rawMin, max: rawMax, includeMin, includeMax, parse, exceptions, error }) => (valueRaw) => {
    if (isEmpty(valueRaw)) {
        return null;
    }

    const toNumber = (rawValue) => {
        if (parse) {
            return parse(rawValue);
        }

        if (typeof rawValue === 'number') {
            return rawValue;
        }

        return Number(String(rawValue).replace(',', '.'));
    };

    const value = toNumber(valueRaw);
    const min = value instanceof Date ? getStartOfTheDay(toNumber(rawMin)) : toNumber(rawMin);
    const max = value instanceof Date ? getEndOfTheDay(toNumber(rawMax)) : toNumber(rawMax);

    const fitsMin = includeMin
        ? value >= min
        : value > min;
    const fitsMax = includeMax
        ? value <= max
        : value < max;
    const fits = fitsMin && fitsMax;
    const isException = exceptions.includes(value);

    return fits !== isException
        ? null
        : [error, [min, max]];
};

export const money = ({ re, error, isZeroAccepted = false }) => {
    const moneyNumber = number({ ...defaults.number, re, error });
    const moneyRange = range({ ...defaults.range, min: 0, includeMin: isZeroAccepted, error });

    return value => moneyNumber(value) || moneyRange(value);
};

export const length = ({ min, max, error }) => (value) => {
    if (isEmpty(value)) {
        return null;
    }

    const valueLength = String(value).length;

    return valueLength >= min && valueLength <= max ? null : [error, [min, max]];
};

export const email = ({ re, error }) => regexp({ re, error });

export const oneOfBy = ({ fields, predicate, error }) => {
    if (fields.length === 0) {
        return () => null;
    }

    return (value, values) => fields.some(fieldName => predicate(values[fieldName]))
        ? null
        : [error, [fields.join(', ')]];
};

export const oneOf = ({ fields, error }) => oneOfBy({ fields, error, predicate: isNotEmpty });

/**
 * Requires field values to be in ascending order
 * @param {Array} fields
 * @param {Boolean} eq=true - allow equality of fields
 * @param {String} error
 * @param {Array} exceptions
 * @returns {Function}
 */
export const ascending = ({ fields, eq, error, exceptions }) => (value, values, fieldName) => {
    if (isEmpty(value) || exceptions.includes(value)) {
        return null;
    }

    const fieldsToValidate = exceptions.length
        ? reject(field => exceptions.includes(values[field]), fields)
        : fields;
    const index = fieldsToValidate.indexOf(fieldName);
    const fieldValue = Number(String(value).replace(',', '.'));

    for (let i = 0; i < fieldsToValidate.length; i++) {
        const val = values[fieldsToValidate[i]];
        if (i === index || isEmpty(val)) {
            continue;
        }

        const otherFieldValue = Number(String(val).replace(',', '.'));
        const isInvalid = eq
            ? i < index && otherFieldValue > fieldValue || i > index && otherFieldValue < fieldValue
            : i < index && otherFieldValue >= fieldValue || i > index && otherFieldValue <= fieldValue;

        if (isInvalid) {
            const errorFields = index > i
                ? [fieldName, fieldsToValidate[i]]
                : [fieldsToValidate[i], fieldName];
            return [error, errorFields];
        }
    }

    return null;
};

export const equal = ({ field, error }) =>
    field == null
        ? () => null
        : (value, values, _, fieldName) => value === values[field] ? null : [error, [fieldName, field]];

export const notEqual = ({ field, error }) =>
    field == null
        ? () => null
        : (value, values, _, fieldName) => value !== values[field] ? null : [error, [fieldName, field]];

export const confirm = ({ field, error }) => {
    if (!field) {
        return () => null;
    }

    return (value, values) => value === values[field] ? null : [error, [field]];
};

/**
 * User defined `userFn` takes `values` as first param and all you've defined in validation config for this rule -
 * as second. It must return `error` message if validation fails or `null` otherwise.
 * @param {Function} fn
 * @param params
 * @param {...} args
 */
export const fn = ({ fn: userFn, ...params }, ...args) => value => userFn(value, params, ...args);

// endregion

export default withDefaultOptions({
    required,
    number,
    int,
    positive,
    negative,
    money,
    length,
    range,
    regexp,
    email,
    oneOf,
    oneOfBy,
    ascending,
    equal,
    notEqual,
    confirm,
    fn
});
